import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "next-themes";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  LayoutDashboard, Radio, Bell, Users, LogOut, ChevronRight, Map, Sun, Moon, Settings as SettingsIcon, KeyRound, BookText, Download, Menu, BarChart3, Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import logo from "@/assets/dosatronic-logo.png";
import { ChatWidget } from "@/components/ChatWidget";

const NavItem = ({ to, icon: Icon, label, testid, end, onClick }) => (
  <NavLink
    to={to}
    end={end}
    data-testid={testid}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
        isActive
          ? "bg-blue-600 text-white"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`
    }
  >
    <Icon className="h-[18px] w-[18px]" strokeWidth={1.75} />
    {label}
  </NavLink>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { t } = useI18n();
  const navigate = useNavigate();
  const isDark = theme === "dark";
  const [mobileOpen, setMobileOpen] = useState(false);

  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const downloadManual = async (lang) => {
    try {
      const res = await api.get(`/manual?lang=${lang}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Dosacloud_Manual_${lang}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t("auth.assistantError"));
    }
  };

  const changePassword = async () => {
    if (pw.next !== pw.confirm) { toast.error(t("auth.passwordsNoMatch")); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success(t("auth.passwordChanged"));
      setPwOpen(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-slate-900/50 md:hidden" onClick={() => setMobileOpen(false)} data-testid="mobile-backdrop" />
      )}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-slate-200 bg-white transition-transform duration-200 md:static md:z-auto md:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center border-b border-slate-200 px-5">
          <div className="inline-flex items-center rounded-md px-2 py-1.5" style={{ backgroundColor: "#ffffff" }}>
            <img src={logo} alt="Dosatronic" className="h-6 w-auto" data-testid="sidebar-logo" />
          </div>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {user?.role !== "cliente" && <NavItem to="/" icon={LayoutDashboard} label={t("nav.dashboard")} testid="nav-dashboard" end onClick={() => setMobileOpen(false)} />}
          {user?.role !== "cliente" && <NavItem to="/stations" icon={Radio} label={t("nav.stations")} testid="nav-estaciones" onClick={() => setMobileOpen(false)} />}
          {user?.role !== "cliente" && <NavItem to="/map" icon={Map} label={t("nav.map")} testid="nav-mapa" onClick={() => setMobileOpen(false)} />}
          {user?.role !== "cliente" && <NavItem to="/alerts" icon={Bell} label={t("nav.alerts")} testid="nav-alarmas" onClick={() => setMobileOpen(false)} />}
          <NavItem to="/productos" icon={BookText} label={t("nav.products")} testid="nav-productos" onClick={() => setMobileOpen(false)} />
          {["admin", "avanzado"].includes(user?.role) && <NavItem to="/users" icon={Users} label={t("nav.users")} testid="nav-usuarios" onClick={() => setMobileOpen(false)} />}
          {user?.role === "admin" && <NavItem to="/admin/productos" icon={Package} label={t("nav.manageProducts")} testid="nav-manage-products" onClick={() => setMobileOpen(false)} />}
          {user?.role === "admin" && <NavItem to="/analytics" icon={BarChart3} label={t("nav.analytics")} testid="nav-analytics" onClick={() => setMobileOpen(false)} />}
          {user?.role === "admin" && <NavItem to="/settings" icon={SettingsIcon} label={t("nav.settings")} testid="nav-settings" onClick={() => setMobileOpen(false)} />}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="mb-3 flex items-center justify-between px-3">
            <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400">Idioma</span>
            <LanguageSwitcher />
          </div>
          <Button
            variant="ghost"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            data-testid="theme-toggle"
            className="mb-1 w-full justify-start gap-3 text-slate-600 hover:bg-slate-100"
          >
            {isDark ? <Sun className="h-[18px] w-[18px]" strokeWidth={1.75} /> : <Moon className="h-[18px] w-[18px]" strokeWidth={1.75} />}
            {isDark ? t("common.lightMode") : t("common.darkMode")}
          </Button>
          <div className="mb-2 flex items-center gap-3 rounded-md px-3 py-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-50 text-xs font-semibold text-blue-700">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-sm font-medium text-slate-800">{user?.name}</div>
              <div className="truncate text-xs capitalize text-slate-400">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={() => setManualOpen(true)}
            data-testid="manual-btn"
            className="mb-1 w-full justify-start gap-3 text-slate-600 hover:bg-slate-100"
          >
            <BookText className="h-[18px] w-[18px]" strokeWidth={1.75} />
            {t("auth.helpMenu")}
          </Button>
          <Button
            variant="ghost"
            onClick={() => { setMobileOpen(false); setPwOpen(true); }}
            data-testid="change-password-btn"
            className="mb-1 w-full justify-start gap-3 text-slate-600 hover:bg-slate-100"
          >
            <KeyRound className="h-[18px] w-[18px]" strokeWidth={1.75} />
            {t("auth.changePassword")}
          </Button>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-3 text-slate-600 hover:text-rose-600"
          >
            <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} />
            {t("common.logout")}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 md:hidden">
          <button onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn" aria-label="Menu" className="text-slate-600">
            <Menu className="h-6 w-6" strokeWidth={1.75} />
          </button>
          <div className="inline-flex items-center rounded-md px-2 py-1" style={{ backgroundColor: "#ffffff" }}>
            <img src={logo} alt="Dosatronic" className="h-6 w-auto" />
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("auth.changePassword")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("auth.currentPassword")}</Label>
              <Input type="password" data-testid="cp-current" value={pw.current}
                onChange={(e) => setPw({ ...pw, current: e.target.value })} className="mt-1.5" placeholder="••••••••" />
            </div>
            <div>
              <Label>{t("auth.newPassword")}</Label>
              <Input type="password" data-testid="cp-new" value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })} className="mt-1.5" placeholder="••••••••" />
            </div>
            <div>
              <Label>{t("auth.confirmPassword")}</Label>
              <Input type="password" data-testid="cp-confirm" value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1.5" placeholder="••••••••" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>{t("common.cancel")}</Button>
            <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={changePassword} data-testid="cp-submit"
              disabled={pwBusy || !pw.current || !pw.next || pw.next.length < 6}>{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("auth.manualTitle")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">{t("auth.manualDesc")}</p>
          <div className="mt-1 grid gap-2">
            {[["es", "Español"], ["en", "English"], ["de", "Deutsch"]].map(([code, label]) => (
              <Button key={code} variant="outline" onClick={() => downloadManual(code)}
                data-testid={`manual-dl-${code}`} className="justify-between">
                <span>{label}</span>
                <Download className="h-4 w-4" />
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ChatWidget />
    </div>
  );
};

export const PageHeader = ({ title, subtitle, breadcrumb, actions }) => (
  <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/90 px-6 py-4 backdrop-blur">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        {breadcrumb && (
          <div className="mb-1 flex items-center gap-1 text-xs text-slate-400">
            {breadcrumb.map((b, i) => (
              <span key={b?.key || (typeof b === "string" ? b : i)} className="flex items-center gap-1">
                {i > 0 && <ChevronRight className="h-3 w-3" />}
                {b}
              </span>
            ))}
          </div>
        )}
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900" data-testid="page-title">
          {title}
        </h1>
        {subtitle && <p className="mt-0.5 text-sm text-slate-500">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  </div>
);
